<!-- eslint-disable -->

<template>
  <div class="col-12 scenario-row">
    <FormLabel text="Scenario Selection" class="scenario-select">
      <AutoComplete
        v-model="advancedSearch.scenarioSearch"
        class="w-full"
        option-label="title"
        option-value="title"
        :dropdown="true"
        dropdownMode="current"
        :required="false"
        :suggestions="filteredScenarios"
        append-to="self"
        placeholder="Type or Select a scenario to link your quote"
        :loading="fetchingScenarios"
        :disabled="
          !hasWellOrAddressAndCustomer ||
          advancedSearch.status == 'won' ||
          Boolean(quote?.spyglass_scenario_id)
        "
        @complete="completeSearchScenario"
        @item-select="selectAutocomplete"
        @blur="resetScenarioSearch"
      >
        <template #option="slotProps">
          <div class="flex align-options-center">
            {{ slotProps.option.title }} -
            {{
              slotProps.option.journal_created_at
                .slice(0, 10)
                .replaceAll("-", "/")
            }}
            -
            {{ slotProps.option.author.display_name }}
          </div>
        </template>
      </AutoComplete>
    </FormLabel>
    <Button
      v-if="linkedScenarioRoute"
      label="View Sizing"
      class="uniform-height view-sizing-button"
      data-cy="quote-linked-sizing-button"
      v-tooltip.top="`Open the sizing scenario: ${quote?.quote_scenario?.title || ''}`"
      @click="router.push(linkedScenarioRoute)"
    />
  </div>
</template>

<script setup lang="ts">
/* eslint-disable */
// @ts-nocheck

import { computed, ref, toRefs, toRaw, nextTick } from "vue";
import { useRouter } from "vue-router";
import type { AutoCompleteCompleteEvent, AutoCompleteItemSelectEvent } from "primevue/autocomplete";
import AutoComplete from "primevue/autocomplete";
import Button from "primevue/button";

import FormLabel from "@/components/FormLabel.vue";
import { useCanonicalNwaLookup } from "@/composables/quote/useCanonicalNwaLookup";
import {
  buildMaterialQueryFromSpyglassEquipment,
  buildQuotePlanningOrderQueryString,
  buildQuoteWellAddressQueryString,
  buildSizingQuoteErpHeader,
  fixDate,
  normalizeErpId,
  pickQuotePlanningOrder,
  line_item_order,
  materialToQuoteLineItem,
  promiseMap,
  formatQuoteLineItemForSave,
  LineItemType
} from "@/utils/quoteHelpers";
import {
  type Scenarios,
  type ScenariosQuery,
  useScenariosToGenerateQuotesQuery,
  useSkEquipmentTypeConversionsQuery,
  useInsertQuoteMutation,
  useUpdateQuoteByPkMutation,
  useSkWellAddressesQuery,
  useGetSkPlanningOrderQuery,
  useScenariosQuery,
  useGetQuotesCountQuery,
  Wells_Bool_Exp,
  Order_By,
  Quotes_Insert_Input,
  AllQuoteFieldsFragment as QuoteTypeStruct,
  Quote_Line_Items_Insert_Input as QuoteLineItemInputStruct,
  
} from "gql/api";
type ScenarioPreview = ScenariosQuery['scenarios'][number];
import { useToast } from "primevue/usetoast";

// TODO: investigate these type overlaps
// TODO: potentially before double checking these interfaces, check
// materialToQuoteLineItem in quoteHelpers.ts. Need to check if it is
// in sync with MaterialSidePanel's way of creating line items and
// its way of calculating line item types (if applicable)
type QuoteType = Partial<
  { quote_line_items: { data: QuoteLineItem[] } } &
  Omit<QuoteTypeStruct, '__typename' | 'quote_line_items'>
>;

type QuoteLineItem =
  { line_item_type: LineItemType } &
  Partial<
    Omit<QuoteLineItemInputStruct, '__typename' | 'line_item_type'>
  >;

const toast = useToast();
const emit = defineEmits([
  "toggle",
]);

const props = defineProps({
  advancedSearch: {
    type: Object,
    default: {}
  },
  quote: {
    type: Object as PropType<QuoteType | null>,
    default: {}
  },
  refetchQuote: {
    type: Function
  }
});

const { quote, advancedSearch } = toRefs(props);

const hasWellOrAddressAndCustomer = computed(() => {
  const hasWellOrAddress =
    Boolean(advancedSearch.value.wellAddressSearch?.well?.id) ||
    Boolean(advancedSearch.value.wellAddressSearch?.address?.id);
  return (
    hasWellOrAddress &&
    Boolean(advancedSearch.value.wellAddressSearch?.customer?.id)
  );
});

const addingQuoteMessages = ref<string[]>([]);
const scenariosToCreateQuotes = ref<Array<string>>([]);
const well_id = ref("");
const address_id = ref("");
const customer_id = ref("");
const well_uwi = ref("");
const material_search = ref("");

const router = useRouter();

const { executeQuery: getQuotesCount } = useGetQuotesCountQuery({
  requestPolicy: "network-only",
  pause: true,
  variables: {}
});

const {
  data: equipmentTypeConversions,
  executeQuery: fetchSKEquipmentTypeConversions
} = useSkEquipmentTypeConversionsQuery({
  requestPolicy: "network-only",
  variables: {
    summitQueryString: ""
  }
});

fetchSKEquipmentTypeConversions();

const fetchSkMaterials = () => Promise.resolve();

const { executeQuery: fetchERPWellAddress, fetching: fetchingERPWellAddress} = useSkWellAddressesQuery({
  pause: true,
  requestPolicy: "network-only",
  variables: computed(() => {
    const summitQueryString = buildQuoteWellAddressQueryString({
      addressId: address_id.value,
      customerId: customer_id.value,
      wellId: well_id.value,
      wellUwi: well_uwi.value
    });

    return {
      summitQueryString
    };
  })
});

const { executeQuery: fetchScenariosList } = useScenariosToGenerateQuotesQuery({
  pause: scenariosToCreateQuotes.value.length == 0,
  requestPolicy: "network-only",
  variables: {
    where: {
      id: {
        _in: scenariosToCreateQuotes.value
      }
    }
  }
});

// SK-5231: use the customer's planning order (same as the Sales Info form)
// rather than the first SK job from a filter SK ignores.
const planningOrderQueryString = ref("");
const { resolveNwa } = useCanonicalNwaLookup();

// Back-track from the quote to the sizing scenario it was generated from.
const linkedScenarioRoute = computed(() => {
  const scenarioId = quote?.value?.quote_scenario?.id || quote?.value?.spyglass_scenario_id;
  const sizingId = quote?.value?.quote_scenario?.sizing?.id;
  return scenarioId && sizingId
    ? { name: "ScenarioInfo", params: { sizingId, scenarioId } }
    : null;
});
const { executeQuery: fetchPlanningOrder } = useGetSkPlanningOrderQuery({
  pause: true,
  requestPolicy: "network-only",
  variables: computed(() => ({
    summitQueryString: planningOrderQueryString.value
  }))
});

const fetchScenarioPlanningOrder = (wellAddressData) => {
  const queryString = buildQuotePlanningOrderQueryString({
    addressId: address_id.value || wellAddressData?.address?.id,
    customerId: customer_id.value,
    wellId: well_id.value
  });
  if (!queryString) {
    return Promise.resolve({ planningOrder: null, nwaResolution: null });
  }
  planningOrderQueryString.value = queryString;
  return nextTick()
    .then(() => fetchPlanningOrder())
    .then(resp => pickQuotePlanningOrder(resp?.data?.value?.getSKPlanningOrder))
    // Planning orders carry the NWA name stored on old SK jobs (often a
    // retired ECC name); resolve the id against the canonical NWA instead.
    .then(planningOrder => resolveNwa(planningOrder?.nwa_id)
      .then(nwaResolution => ({ planningOrder, nwaResolution })));
};

const { executeMutation: insertQuote } = useInsertQuoteMutation();
const { executeMutation: updateQuote } = useUpdateQuoteByPkMutation();

const filteredScenarios = ref<ScenarioPreview[]>([]);

const { fetching: fetchingScenarios, executeQuery: fetchScenarios } =
  useScenariosQuery({
    pause: true,
    requestPolicy: "network-only",
    variables: computed(() => {
      const address_id = advancedSearch.value.wellAddressSearch?.address?.id;
      const well_id = advancedSearch.value.wellAddressSearch?.well?.id;
      const customerId = advancedSearch.value.wellAddressSearch?.customer?.id ?? "";
      const orFilters: Wells_Bool_Exp[] = [];
      if (address_id) {
        orFilters.push({ address_erp_id: { _eq: address_id } });
      }

      if (well_id) {
        orFilters.push({ well_erp_id: { _eq: well_id } });
      }

      const params = {
        order_by: [{ journal_created_at: Order_By.Desc }],
        withAggregate: true,
        limit: 20,
        offset: 0,
        where: {
          journal_deleted_at: { _is_null: true },
          sizing: {
            customer: {
              customer_erp_id: { _eq: customerId }
            },
            well: {
              _or: orFilters
            }
          }
        }
      };

      return params;
    })
  });

const completeSearchScenario = (event: AutoCompleteCompleteEvent): void => {
  const text = event.query;
  if (text.trim().length === 0) {
    // This is for when dropdown is clicked, trigger query by using space
    advancedSearch.value.scenarioSearch = " ";
  } else {
    advancedSearch.value.scenarioSearch = text.trim();
  }

  fetchScenarios().then((resp) => {
    filteredScenarios.value = resp?.data?.value?.scenarios || [];
    });
};

const removeEmpty = (obj: Record<string, unknown>) => {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != null));
};

const addLineItem = (
  lineItemMap: Record<string, QuoteLineItem[]>,
  lineItem: QuoteLineItem
) => {
  if (lineItemMap[lineItem.line_item_type]) {
    lineItemMap[lineItem.line_item_type].push(lineItem);
  } else {
    lineItemMap[lineItem.line_item_type] = [lineItem];
  }
};

const addQuote = (scenarioId: string) => {
  const etcs = equipmentTypeConversions?.value?.getSKEquipmentTypeConversions;
  addingQuoteMessages.value.push(`Fetching Scenario: ${scenarioId}`);
  scenariosToCreateQuotes.value.push(scenarioId);
  const fetchingScenarios = fetchScenariosList();
  toast.add({
    severity: "info",
    summary: 'Applying scenario',
    detail: 'Please wait for scenario to finish applying.',
    life: 7000
  });
  emit("toggle", true);
  return fetchingScenarios
    .then(resp => {
      addingQuoteMessages.value.push(
        `Received Data For Scenario: ${scenarioId}`
      );
      // "[TBD]" sizing wells store well_erp_id "0"; treat it as no well.
      address_id.value =
        normalizeErpId(resp?.data?.value?.scenarios[0]?.sizing?.well?.address_erp_id) || "";
      well_id.value =
        normalizeErpId(resp?.data?.value?.scenarios[0]?.sizing?.well?.well_erp_id) || "";
      customer_id.value =
        normalizeErpId(resp?.data?.value?.scenarios[0]?.sizing?.customer?.customer_erp_id) || "";
      well_uwi.value =
        resp?.data?.value?.scenarios[0]?.sizing?.well?.well_uwi  ?? "";

      const quotes = (resp?.data?.value?.scenarios || []).map(scenario => {
        const quoteObj: QuoteType = {
          quote_line_items: { data: [] }
        };
        addingQuoteMessages.value.push(
          `Fetching Data For Address: : ${address_id.value}`
        );
        const hasWellOrAddress = Boolean(customer_id.value && (address_id.value || well_id.value));
        const fetchingWellAddress = hasWellOrAddress
          ? nextTick().then(() => fetchERPWellAddress())
          : Promise.resolve(null);

        return fetchingWellAddress.then(_resp => {
          const wellAddressData = _resp?.data?.value?.getSKWellAddresses?.[0];
          addingQuoteMessages.value.push(
            `Received Data For Address: : ${address_id.value || wellAddressData?.address?.id || ""}`
          );
          addingQuoteMessages.value.push(`Fetching PLO Data For Well/Address & Customer.`);
          return Promise.all([
            fetchScenarioPlanningOrder(wellAddressData),
            getQuotesCount()
          ]).then(([{ planningOrder, nwaResolution }, quotesCountResp]) => {
            const proposal_count =
              1000001 + (quotesCountResp?.data?.value?.quotes_aggregate?.aggregate?.count ?? 0);
            const skProposalNumber = `SG${proposal_count}`;
            addingQuoteMessages.value.push(
              planningOrder ? `Received Data For PLO.` : `No PLO found for Well/Address & Customer.`
            );
            Object.assign(quoteObj, {
              ...buildSizingQuoteErpHeader({
                addressId: address_id.value,
                customerId: customer_id.value,
                planningOrder,
                today: fixDate(new Date()),
                wellAddress: wellAddressData,
                wellId: well_id.value,
                nwa: nwaResolution,
                sizingCustomerName: scenario?.sizing?.customer?.customer_name,
                sizingWellName: scenario?.sizing?.well?.well_name,
                sizingWellUwi: scenario?.sizing?.well?.well_uwi
              }),
              name: scenario.title,
              // "note": "",
              prepared_by_id: scenario?.author?.id,
              sk_proposal_number: skProposalNumber,
              spyglass_scenario_id: scenario?.id,
              spyglass_scenario_import_json: JSON.stringify(scenario),
              // "sk_job_type": job?.job_type?.material_number || job?.job_type?.material_number,
              version_number: 1,
              source: "scenario_autocomplete",
              quote_line_items: {
                data: []
              }
            });
            
            const existingQuote = toRaw(quote.value) || {};
            const existingLineItems = existingQuote.quote_line_items || [];
            const mappedLineItems = (Object as any).groupBy(
              existingLineItems,
              ({ line_item_type }: { line_item_type: LineItemType }) => line_item_type
            );
            const newLineItemList = [];
            if (existingQuote?.quote_line_items) {
              delete existingQuote.quote_line_items;
            }
            Object.assign(quoteObj, removeEmpty(existingQuote));
            delete (quoteObj as any).__typename;

            if (scenario?.equipment?.r_xref_pumps?.length) {
              addingQuoteMessages.value.push(`Adding pumps to quote.`);
            }

            const pumps = (scenario?.equipment?.r_xref_pumps ?? [])
              ?.map(pump => {
                const housings_order = pump.housings_order;
                return pump.r_xref_housings.map(housing => {
                  if (housings_order?.includes(housing.id)) {
                    return housing;
                  }
                  return null;
                });
              })
              .flat()
              .filter(Boolean);

            return promiseMap(pumps, pump => {
              const materialObjectQuery =
                buildMaterialQueryFromSpyglassEquipment(
                  LineItemType.PUMP,
                  pump,
                  etcs,
                  quoteObj,
                  scenario,
                  material_search
                );
              material_search.value = materialObjectQuery.searchValue;
              addingQuoteMessages.value.push(
                `Fetching pump material: ${material_search.value}`
              );
              return Promise.resolve()
                .then(fetchSkMaterials)
                .then(materialResp => {
                  addLineItem(
                    mappedLineItems,
                    materialToQuoteLineItem(
                      materialResp,
                      materialObjectQuery,
                      "scenario_autocomplete",
                      quoteObj,
                      null
                    )
                  );
                });
            })
              .then(() => {
                if (scenario?.equipment?.r_motor) {
                  addingQuoteMessages.value.push(`Adding motor to quote.`);
                }
                return promiseMap(
                  [scenario?.equipment?.r_motor].filter(Boolean),
                  motor => {
                    const materialObjectQuery =
                      buildMaterialQueryFromSpyglassEquipment(
                        LineItemType.MOTOR,
                        motor,
                        etcs,
                        quoteObj,
                        scenario,
                        material_search
                      );
                    material_search.value = materialObjectQuery.searchValue;
                    addingQuoteMessages.value.push(
                      `Fetching motor material: ${material_search.value}`
                    );
                    return Promise.resolve()
                      .then(fetchSkMaterials)
                      .then(materialResp => {
                        addLineItem(
                          mappedLineItems,
                          materialToQuoteLineItem(
                            materialResp,
                            materialObjectQuery,
                            "scenario_autocomplete",
                            quoteObj,
                            null
                          )
                        );
                      });
                  }
                );
              })
              .then(() => {
                if (scenario?.equipment?.r_xref_intakes?.length) {
                  addingQuoteMessages.value.push(`Adding intakes to quote.`);
                }
                return promiseMap(
                  scenario?.equipment?.r_xref_intakes ?? [],
                  intake => {
                    const materialObjectQuery =
                      buildMaterialQueryFromSpyglassEquipment(
                        LineItemType.INTAKE,
                        intake,
                        etcs,
                        quoteObj,
                        scenario,
                        material_search
                      );
                    material_search.value = materialObjectQuery.searchValue;
                    addingQuoteMessages.value.push(
                      `Fetching intake material: ${material_search.value}`
                    );
                    return Promise.resolve()
                      .then(fetchSkMaterials)
                      .then(materialResp => {
                        addLineItem(
                          mappedLineItems,
                          materialToQuoteLineItem(
                            materialResp,
                            materialObjectQuery,
                            "scenario_autocomplete",
                            quoteObj,
                            null
                          )
                        );
                      });
                  }
                );
              })
              .then(() => {
                if (scenario?.equipment?.r_xref_seals?.length) {
                  addingQuoteMessages.value.push(`Adding seals to quote.`);
                }
                return promiseMap(scenario?.equipment?.r_xref_seals ?? [], seal => {
                  const materialObjectQuery =
                    buildMaterialQueryFromSpyglassEquipment(
                      LineItemType.SEAL,
                      seal,
                      etcs,
                      quoteObj,
                      scenario,
                      material_search
                    );
                  material_search.value = materialObjectQuery.searchValue;
                  addingQuoteMessages.value.push(
                    `Fetching seal material: ${material_search.value}`
                  );
                  return Promise.resolve()
                    .then(fetchSkMaterials)
                    .then(materialResp => {
                      addLineItem(
                        mappedLineItems,
                        materialToQuoteLineItem(
                          materialResp,
                          materialObjectQuery,
                          "scenario_autocomplete",
                          quoteObj,
                          null
                        )
                      );
                    });
                });
              })
              .then(() => {
                if (scenario?.equipment?.r_sensor) {
                  addingQuoteMessages.value.push(`Adding sensor to quote.`);
                }
                return promiseMap(
                  [scenario?.equipment?.r_sensor].filter(Boolean),
                  sensor => {
                    const materialObjectQuery =
                      buildMaterialQueryFromSpyglassEquipment(
                        LineItemType.SENSOR,
                        sensor,
                        etcs,
                        quoteObj,
                        scenario,
                        material_search
                      );
                    material_search.value = materialObjectQuery.searchValue;
                    addingQuoteMessages.value.push(
                      `Fetching sensor material: ${material_search.value}`
                    );
                    return Promise.resolve()
                      .then(fetchSkMaterials)
                      .then(materialResp => {
                        addLineItem(
                          mappedLineItems,
                          materialToQuoteLineItem(
                            materialResp,
                            materialObjectQuery,
                            "scenario_autocomplete",
                            quoteObj,
                            null
                          )
                        );
                      });
                  }
                );
              })
              .then(() => {
                if (scenario?.equipment?.r_mle) {
                  addingQuoteMessages.value.push(
                    `Adding motor lead extension to quote.`
                  );
                }
                return promiseMap(
                  [scenario?.equipment?.r_mle].filter(Boolean),
                  mle => {
                    const materialObjectQuery =
                      buildMaterialQueryFromSpyglassEquipment(
                        LineItemType.MLE,
                        mle,
                        etcs,
                        quoteObj,
                        scenario,
                        material_search
                      );
                    material_search.value = materialObjectQuery.searchValue;
                    addingQuoteMessages.value.push(
                      `Fetching motor lead extension material: ${material_search.value}`
                    );
                    return Promise.resolve()
                      .then(fetchSkMaterials)
                      .then(materialResp => {
                        addLineItem(
                          mappedLineItems,
                          materialToQuoteLineItem(
                            materialResp,
                            materialObjectQuery,
                            "scenario_autocomplete",
                            quoteObj,
                            null
                          )
                        );
                      });
                  }
                );
              })
              .then(() => {
                if (scenario?.equipment?.r_xref_cables?.length) {
                  addingQuoteMessages.value.push(`Adding cables to quote.`);
                }
                return promiseMap(scenario?.equipment?.r_xref_cables ?? [], cable => {
                  const materialObjectQuery =
                    buildMaterialQueryFromSpyglassEquipment(
                      LineItemType.CABLE,
                      cable,
                      etcs,
                      quoteObj,
                      scenario,
                      material_search
                    );
                  material_search.value = materialObjectQuery.searchValue;
                  addingQuoteMessages.value.push(
                    `Fetching cable material: ${material_search.value}`
                  );
                  return Promise.resolve()
                    .then(fetchSkMaterials)
                    .then(materialResp => {
                      addLineItem(
                        mappedLineItems,
                        materialToQuoteLineItem(
                          materialResp,
                          materialObjectQuery,
                          "scenario_autocomplete",
                          quoteObj,
                          null
                        )
                      );
                    });
                });
              })
              .then(() => {
                if (scenario?.equipment?.r_switchboard) {
                  addingQuoteMessages.value.push(
                    `Adding switchboard/surface drive to quote.`
                  );
                }

                let switchboards = [scenario?.equipment?.r_switchboard].filter(
                  Boolean
                );

                if (scenario?.equipment?.use_vsd) {
                  switchboards = [];
                }

                return promiseMap(switchboards, switchboard => {
                  const materialObjectQuery =
                    buildMaterialQueryFromSpyglassEquipment(
                      "switchboard",
                      switchboard,
                      etcs,
                      quoteObj,
                      scenario,
                      material_search
                    );
                  material_search.value = materialObjectQuery.searchValue;
                  addingQuoteMessages.value.push(
                    `Fetching switchboard surface drive material: ${material_search.value}`
                  );
                  return Promise.resolve()
                    .then(fetchSkMaterials)
                    .then(materialResp => {
                      addLineItem(
                        mappedLineItems,
                        materialToQuoteLineItem(
                          materialResp,
                          materialObjectQuery,
                          "scenario_autocomplete",
                          quoteObj,
                          null
                        )
                      );
                    });
                });
              })
              .then(() => {
                if (scenario?.equipment?.r_vsd) {
                  addingQuoteMessages.value.push(
                    `Adding variable speed drive to quote.`
                  );
                }
                let vsds = [scenario?.equipment?.r_vsd].filter(Boolean);

                if (!scenario?.equipment?.use_vsd) {
                  vsds = [];
                }

                return promiseMap(vsds, vsd => {
                  const materialObjectQuery =
                    buildMaterialQueryFromSpyglassEquipment(
                      "vsd",
                      vsd,
                      etcs,
                      quoteObj,
                      scenario,
                      material_search
                    );
                  material_search.value = materialObjectQuery.searchValue;
                  addingQuoteMessages.value.push(
                    `Fetching variable speed drive material: ${material_search.value}`
                  );
                  return Promise.resolve()
                    .then(fetchSkMaterials)
                    .then(materialResp => {
                      addLineItem(
                        mappedLineItems,
                        materialToQuoteLineItem(
                          materialResp,
                          materialObjectQuery,
                          "scenario_autocomplete",
                          quoteObj,
                          null
                        )
                      );
                    });
                });
              })
              .then(() => {
                if (scenario?.equipment?.r_step_up_transformer) {
                  addingQuoteMessages.value.push(
                    `Adding Step Up Transformer to quote.`
                  );
                }
                return promiseMap(
                  [scenario?.equipment?.r_step_up_transformer].filter(Boolean),
                  step_up_transformer => {
                    const materialObjectQuery =
                      buildMaterialQueryFromSpyglassEquipment(
                        LineItemType.STEP_UP_TRANSFORMER,
                        step_up_transformer,
                        etcs,
                        quoteObj,
                        scenario,
                        material_search
                      );
                    material_search.value = materialObjectQuery.searchValue;
                    addingQuoteMessages.value.push(
                      `Fetching step up transformer material: ${material_search.value}`
                    );
                    return Promise.resolve()
                      .then(fetchSkMaterials)
                      .then(materialResp => {
                        addLineItem(
                          mappedLineItems,
                          materialToQuoteLineItem(
                            materialResp,
                            materialObjectQuery,
                            "scenario_autocomplete",
                            quoteObj,
                            null
                          )
                        );
                      });
                  }
                );
              })
              .then(() => {
                if (scenario?.equipment?.r_step_down_transformer) {
                  addingQuoteMessages.value.push(
                    `Adding Step Down Transformer to quote.`
                  );
                }
                return promiseMap(
                  [scenario?.equipment?.r_step_down_transformer].filter(
                    Boolean
                  ),
                  step_down_transformer => {
                    const materialObjectQuery =
                      buildMaterialQueryFromSpyglassEquipment(
                        LineItemType.STEP_DOWN_TRANSFORMER,
                        step_down_transformer,
                        etcs,
                        quoteObj,
                        scenario,
                        material_search
                      );
                    material_search.value = materialObjectQuery.searchValue;
                    addingQuoteMessages.value.push(
                      `Fetching step down transformer material: ${material_search.value}`
                    );
                    return Promise.resolve()
                      .then(fetchSkMaterials)
                      .then(materialResp => {
                        addLineItem(
                          mappedLineItems,
                          materialToQuoteLineItem(
                            materialResp,
                            materialObjectQuery,
                            "scenario_autocomplete",
                            quoteObj,
                            null
                          )
                        );
                      });
                  }
                );
              })
              .then(() => {
                quoteObj.quote_line_items!.data = line_item_order
                  .map(line_item_type => mappedLineItems[line_item_type as LineItemType] ?? [])
                  .flat()
                  .filter(Boolean)
                  .map((line_item, index) => {
                    line_item.sub_order = index + 1;
                    return line_item;
                  });
                return quoteObj;
              });
          });
        });
      });
      return Promise.all(quotes);
    })
    .then(qs => {
      addingQuoteMessages.value.push(`Saving Quote.`);
      scenariosToCreateQuotes.value = [];

      if (!quote?.value?.id) {
        return insertQuote({ object: qs[0] as unknown as Quotes_Insert_Input })
          .finally(() => {
            toast.add({
              severity: "success",
              summary: 'Scenario applied.',
              detail: 'Successfully created new quote with selected scenario.',
              life: 7000
            });
            emit("toggle", false);
          });
      }

      const createdLineItems = qs[0].quote_line_items?.data
        .filter(lineItem => !lineItem.id)
        .map(item => formatQuoteLineItemForSave(item, quote?.value?.id));

      const updatedLineItems = qs[0].quote_line_items?.data
        .filter(lineItem => lineItem.id)
        .map(item => formatQuoteLineItemForSave(item, quote?.value?.id))
        .map(item => {
          return {
            _set: item,
            where: {
              id: { _eq: item.id }
            }
          };
        });

      delete qs[0].quote_scenario;
      delete (qs[0] as any).quote_line_items;
      delete qs[0].user;

      return updateQuote({
        id: quote.value.id,
        object: qs[0],
        create_line_items: createdLineItems ?? [],
        update_line_items: updatedLineItems ?? []
      }).finally(() => {
        toast.add({
          severity: "success",
          summary: 'Scenario applied.',
          detail: 'Successfully updated quote with selected scenario.',
          life: 7000
        });
        emit("toggle", false);
      });
    })
    .then(____resp => {
      if (quote?.value?.id) {
        props.refetchQuote?.();
      }
      const _quoteId = ____resp?.data?.insert_quotes_one?.id;
      if (_quoteId) {
        router.push({
          name: "EditQuote",
          params: {
            quoteId: _quoteId
          }
        }).then(() => {
          props.refetchQuote?.();
        });
      }
    });
};

const selectAutocomplete = (event: AutoCompleteItemSelectEvent) => {
  advancedSearch.value.scenarioSearch = event.value; // Prevents the unblur from triggering / resetting it to blank after selecting an item
  return addQuote(event.value.id);
};

const resetScenarioSearch = () => {
  // Only one value can ever be selected, so if users are looking for their first scenario, we can just reset it to blank (no need to store duplicate variables)
  if (typeof advancedSearch.value.scenarioSearch !== "object") {
    advancedSearch.value.scenarioSearch = "";
  }
};
</script>

<style scoped lang="scss">
:deep(.p-autocomplete .p-inputtext) {
  text-overflow: ellipsis;
}

// Scenario selector and View Sizing button share the form column.
.scenario-row {
  display: flex;
  align-items: flex-end;
  gap: 1rem;
}

.scenario-select {
  flex: 1 1 auto;
  min-width: 0;

  :deep(.p-autocomplete) {
    width: 100%;
    min-width: 0;
  }

  :deep(.p-autocomplete-input) {
    min-width: 0;
    width: 100%;
  }
}

.view-sizing-button {
  flex: 0 0 auto;
  white-space: nowrap;
}

.uniform-height {
  height: 48px !important;
  min-height: 48px !important;
  font-size: 16px;
  box-sizing: border-box;
}
</style>
